import React, { useState, useEffect } from 'react';

import cookie from 'react-cookies';
import { NotificationManager } from 'react-notifications';
import { useNavigate, useLocation } from 'react-router-dom';

import TenantConfig, { getTenant } from '../config';
import {
  APPLICATION_STATUS,
  doesApplicationHaveWIPSections,
  isApplicationWIP,
  PARTNER_STATUS,
} from '../constants/status';
import { isPrimaryApplicant, isPropertyInSection } from '../helpers/applicationHelp';
import { submitClientInfo } from '../helpers/clientInfo';
import mock from '../mock/multi-property-screen.json';
import { getLoanProductInfo } from '../pages/cavenuesgrant/Landing';
import { getProfile, getApplication } from '../service/api';

export const UserContext = React.createContext();

function UserProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [profile, setProfile] = useState({});
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(cookie.load('lendistry_tokens_access'));
  const [plaidToken, setPlaidToken] = useState('');
  const [width, setWidth] = useState(window.innerWidth);
  const [isLoan, setIsLoan] = useState(false);
  const [showSessionExtendModal, setShowSessionExtendModal] = useState(false);

  const storageData = '';
  const [appData, setAppData] = useState(storageData ? JSON.parse(storageData) : {});
  const landingHeaderBeakpoint = TenantConfig.header.breakpoint ?? 660;
  const authHeaderBeakpoint = 800;
  const [intervalObj, setIntervalObj] = useState(null);

  const isLoanFunded = () => {
    let loanFunded = false;
    const approvedRateProperty = appData.applicationFeatures?.find(
      (feature) => feature.itemType === 'ApprovedRateProperty'
    );
    if (approvedRateProperty) {
      loanFunded =
        approvedRateProperty?.value && approvedRateProperty.value.finalApprovedAmount != 0;
    } else {
      const approvedRateProperty = appData.applicationFeatures?.find(
        (feature) => feature.itemType === 'MultiApprovedRateProperty'
      );
      if (
        approvedRateProperty?.value?.rates &&
        approvedRateProperty?.value?.rates[0].approvedRate.approvedAmount > 0
      ) {
        loanFunded = true;
      }
    }
    return loanFunded;
  };
  /** For Rendering different components based on breakpoint */
  useEffect(() => {
    function handleResize() {
      setWidth(window.innerWidth);
    }

    window.addEventListener('resize', handleResize);

    return (_) => {
      window.removeEventListener('resize', handleResize);
    };
  });

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const res = await getApplication();

        if (
          !res &&
          location.pathname !== 'landing' &&
          !location.pathname.startsWith('/auth') &&
          !location.pathname.startsWith('/invite') &&
          location.pathname !== '/dashboard' &&
          profile.email
        ) {
          navigate('/dashboard');
        }

        if (res && res.data) {
          updateAppData(res.data);
        }
        setLoading(false);
      } catch (e) {
        console.error(e);
        if (
          location.pathname !== 'landing' &&
          !location.pathname.startsWith('/auth') &&
          !location.pathname.startsWith('/invite') &&
          location.pathname !== '/dashboard' &&
          profile.email
        ) {
          navigate('/dashboard');
        }

        if (profile?.email) {
          NotificationManager.warning(
            'Sorry we could not load your application, please try to refresh the page',
            'Warning'
          );
        }
        updateAppData({});
        setLoading(false);
      }
    }

    async function getProductInfo() {
      const response = await getLoanProductInfo();
      if (!response.productCode.includes('GRANT')) {
        setIsLoan(true);
      }
    }

    if (!Object.keys(appData).length) {
      fetchData();
    }
    getProductInfo();
  }, []);

  /** Update the appData for both state and local storage */
  const updateAppData = (data) => {
    setAppData(data);
  };

  /** On route change, check the status and redirect logic */
  useEffect(() => {
    if (process.env.REACT_APP_USE_MOCK !== 'true' && cookie.load('lendistry_tokens_access')) {
      getProfile()
        .then((res) => {
          if (res && res.data) {
            cookie.save('lendistry_profile', res.data, {
              path: '/',
            });
            res.data.sub = res.data.id;
            setProfile(res.data);
            submitClientInfo(res.data);
            // setLoading(false);
          }
        })
        .catch((e) => {
          console.log(e);
          if (location.pathname === '/') {
            navigate('/landing');
            return;
          }
          if (
            !location.pathname.startsWith('/auth') &&
            !location.pathname.startsWith('/landing') &&
            !location.pathname.startsWith('/offer') &&
            !location.pathname.startsWith('/invite') &&
            !location.pathname.startsWith('/application-confirm') &&
            !location.pathname.startsWith('/error')
          ) {
            navigate(`/auth/sign-in?app=${location.pathname}`);
            return;
          }
        });
    } else {
      setLoading(false);
    }

    if (
      location.pathname === '/dashboard' ||
      location.pathname === '/profile' ||
      location.pathname.startsWith('/oauth/plaid')
    ) {
      return;
    }
    const loanFunded = isLoanFunded();

    const may_qualify_visited = cookie.load(`${appData.id}_may_qualify_visited`);
    const applicationEditable =
      isApplicationWIP(appData.status) ||
      (TenantConfig.general.checkApplicationPhases == true &&
        doesApplicationHaveWIPSections(appData));
    // If we are in edit mode and the user try to edit the app we let it go
    const isEdit = new URLSearchParams(location.search).get('edit');
    if (applicationEditable && isEdit) {
      return null;
    }

    let hasInProgressFeature = false;

    if (appData.sections && appData.properties) {
      appData.sections.map((section, sectionIndex) => {
        appData.properties
          .filter((property) => isPropertyInSection(appData.features, section, property))
          .map((property, propertyIndex) => {
            const feature = features.find((feature) => feature.name === property);
            if (lIsMainApplicant && feature.valueValidated === false && !hasInProgressFeature) {
              hasInProgressFeature = true;
            }
          });
      });
    }

    const navigateToApplication = () => {
      if (!hasInProgressFeature && location.pathname == '/review') {
        // When all the features are validated and I am on review page, prevent redirecting to the PageForm
        return;
      }
      return navigate(`/product/${appData.loanProductId}/application/${appData.id}`);
    };

    if (profile?.email) {
      switch (appData.status) {
        case APPLICATION_STATUS.BORROWER_CHANGES_REQUIRED:
        case APPLICATION_STATUS.INPROGRESS:
        case APPLICATION_STATUS.CREATED:
          if (
            !location.pathname.startsWith(`/product/${appData.loanProductId}/application/`) &&
            !location.pathname.startsWith('/apply') &&
            !location.pathname.startsWith('/application/') &&
            !location.pathname.startsWith('/profile')
          ) {
            return navigateToApplication();
          }
          return null;
        case APPLICATION_STATUS.AUTOMATED_REVIEW_INPROGRESS:
        case APPLICATION_STATUS.AWAITING_ADDITIONAL_SYSTEM_DATA:
          return navigate('/automated-review');
        case APPLICATION_STATUS.TECHNICAL_REVIEW:
          if (TenantConfig?.settings.redirectUrls.technicalReview) {
            return navigate(TenantConfig?.settings.redirectUrls.technicalReview);
          } else {
            return navigate('/review-in-progress');
          }
        case APPLICATION_STATUS.MANUAL_REVIEW_INPROGRESS:
        case APPLICATION_STATUS.MANUAL_REVIEW_REQUIRED:
          if (TenantConfig?.settings.redirectUrls.manualReviewRequired) {
            return navigate(TenantConfig?.settings.redirectUrls.manualReviewRequired);
          }
          if (applicationEditable) {
            return navigateToApplication();
          } else {
            return navigate('/review-in-progress');
          }
        case APPLICATION_STATUS.AUTOMATED_REVIEW_APPROVED:
        case APPLICATION_STATUS.MANUAL_REVIEW_APPROVED:
          if (
            appData.partnerStatus == PARTNER_STATUS.PARTNER_VERIFICATION_COMPLETED ||
            appData.partnerStatus == PARTNER_STATUS.PARTNER_VERIFICATION_NOT_APPLICABLE
          ) {
            if (getTenant() === 'lightrx') {
              return navigate(`/product/${appData.loanProductId}/application/${appData.id}`);
            } else {
              //Here we look if it docusign
              //if yes we look if the user has already signed for multi signature programs
              //if arleady signed we redirec to the dashboard else we redirect to the docusign...
              if (
                TenantConfig?.settings.redirectUrls.manualReviewApprovedNoPartnerVerif.includes(
                  '/docusign'
                )
              ) {
                const contractProp = appData.applicationFeatures.find(
                  (feature) => feature.itemType === 'ContractDocumentProperty'
                );
                if (contractProp?.value) {
                  const userSignature = contractProp.value.signers?.filter((signature) => {
                    return (
                      signature.status == 'completed' &&
                      signature.emailAddress.toLowerCase() == profile?.email.toLowerCase()
                    );
                  });
                  if (userSignature?.length > 0) {
                    return navigate('/dashboard');
                  }
                }
              }
              return navigate(
                TenantConfig?.settings.redirectUrls.manualReviewApprovedNoPartnerVerif
              );
            }
            //
          } else if (appData.partnerStatus === PARTNER_STATUS.PARTNER_VERIFICATION_FAILED) {
            return navigate(`/application/${appData.id}/lock-failed`);
          } else {
            if (getTenant() === 'lightrx') {
              return navigate(`/product/${appData.loanProductId}/application/${appData.id}`);
            } else return navigate('/approved');
          }
        case APPLICATION_STATUS.AUTOMATED_REVIEW_DECLINED:
        case APPLICATION_STATUS.MANUAL_REVIEW_DECLINED:
        case APPLICATION_STATUS.DENIED:
          return navigate('/denied');
        case APPLICATION_STATUS.AUTOMATED_REVIEW_ADDITIONAL_DOCS_REQUIRED:
        case APPLICATION_STATUS.MANUAL_REVIEW_DOCS_REQUIRED:
        case APPLICATION_STATUS.AUTOMATED_REVIEW_DOCS_REQUIRED:
        case APPLICATION_STATUS.PENDING_SELECTION_DOCS_REQUIRED:
        case APPLICATION_STATUS.MANUAL_REVIEW_DOCS_REQUIRED_SALES:
          if (may_qualify_visited === 'visited') {
            if (applicationEditable) {
              return navigateToApplication();
            } else {
              return navigate('/additional-docs');
            }
          } else return navigate(TenantConfig?.settings.redirectUrls.docsRequired);

        case APPLICATION_STATUS.BORROWER_AGREEMENT_ACCEPTED:
          if (!isLoan) {
            return navigate(
              TenantConfig?.settings.redirectUrls.funded
                ? TenantConfig?.settings.redirectUrls.funded
                : '/funded'
            );
          } else {
            if (TenantConfig?.settings.redirectUrls.borrowerAgreementAccepted) {
              if (TenantConfig?.settings.redirectUrls.borrowerAgreementAccepted == 'APPLICATION') {
                return navigate(`/product/${appData.loanProductId}/application/${appData.id}`);
              } else {
                return navigate(TenantConfig?.settings.redirectUrls.borrowerAgreementAccepted);
              }
            } else {
              return navigate('/dashboard');
            }
          }
        case APPLICATION_STATUS.CLOSED:
          if (loanFunded) {
            return navigate(
              TenantConfig?.settings.redirectUrls.funded
                ? TenantConfig?.settings.redirectUrls.funded
                : '/funded'
            );
          } else {
            return null;
          }
        case APPLICATION_STATUS.PENDING_SELECTION:
          const isEdit = new URLSearchParams(location.search).get('edit');
          //we need to check the user is trying to add the application
          //if yes we only allow section for bank & documents
          if (location.pathname.startsWith('/review')) {
            return null;
          } else if (location.pathname.startsWith('/application/') && isEdit) {
            const sections = appData.sectionSequence;
            const sectionIndex = new URLSearchParams(location.search).get('sectionIndex');
            const selectedFeature = appData.applicationFeatures.find(
              (feature) => feature.section == sections[sectionIndex]
            );
            if (
              ['FileProperty', 'PlaidTokenProperty', 'FinancialInstrumentProperty'].includes(
                selectedFeature.itemType
              )
            )
              return null;
          } else {
            return navigate('/awaiting-selection');
          }

        case APPLICATION_STATUS.MANUAL_REVIEW_REQUIRED_SALES:
          if (may_qualify_visited === 'visited' || !isPrimaryApplicant(appData)) {
            if (applicationEditable) {
              return navigateToApplication();
            } else {
              return navigate(TenantConfig?.settings.redirectUrls.manualReviewRequiredSales);
            }
          } else return navigate(TenantConfig?.settings.redirectUrls.manualReviewRequiredSales);
        case APPLICATION_STATUS.SUSPENSE:
          return navigate(TenantConfig?.settings.redirectUrls.suspense);
        case APPLICATION_STATUS.MANUAL_REVIEW_APPROVED_WAITLISTED:
          return navigate('/wait-listed');
        case APPLICATION_STATUS.MANUAL_REVIEW_REQUIRED_DEPOSIT_VERIFICATION:
          return navigate(
            TenantConfig?.settings.redirectUrls.manualReviewRequiredDepositVerification
          );
        default:
          return null;
      }
    }
  }, [appData, profile?.email, location.pathname]);

  /** On route change, clear existing interval object */
  useEffect(() => {
    clearInterval(intervalObj);
  }, [location.pathname]);

  return (
    <UserContext.Provider
      value={{
        profile,
        setProfile,
        loading,
        setLoading,
        token,
        setToken,
        plaidToken,
        setPlaidToken,
        appData: process.env.REACT_APP_USE_MOCK === 'true' ? mock : appData,
        setAppData,
        updateAppData,
        width,
        setWidth,
        landingHeaderBeakpoint,
        showSessionExtendModal,
        setShowSessionExtendModal,
        authHeaderBeakpoint,
        isLoan,
        intervalObj,
        setIntervalObj,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

export default UserProvider;
